import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, endpoints } from '../api';
import type { Package, PackageWithGallery, PaginatedPackageResponse } from '../types/api';

// Hook for fetching paginated packages (optimized for Admin List)
export const usePaginatedPackages = (params?: {
  page?: number;
  limit?: number;
  search?: string;
  country_id?: number;
  package_type?: string;
  include_inactive?: boolean;
  order_by?: string;
  order?: string;
}) => {
  const queryParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });
  }

  const queryString = queryParams.toString();
  const endpoint = queryString ? `${endpoints.packages.paginated(queryString)}` : endpoints.packages.paginated();

  return useQuery<PaginatedPackageResponse>({
    queryKey: ['packages', 'paginated', params],
    queryFn: async () => {
      return apiClient.get<PaginatedPackageResponse>(endpoint);
    },
  });
};

// Hook for fetching all packages
export const usePackages = (params?: {
  country_id?: number;
  holiday_type_id?: number;
  package_type?: string;
  min_price?: number;
  max_price?: number;
  min_duration?: number;
  max_duration?: number;
  skip?: number;
  limit?: number;
  order_by?: string;
  order?: string;
}) => {
  // Build query string from params
  const queryParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });
  }

  const queryString = queryParams.toString();
  const endpoint = queryString ? `${endpoints.packages.list()}?${queryString}` : endpoints.packages.list();

  return useQuery<Package[]>({
    queryKey: ['packages', params],
    queryFn: async () => {
      return apiClient.get<Package[]>(endpoint);
    },
  });
};

// Hook for fetching featured packages
export const useFeaturedPackages = (limit: number = 10, packageType?: string) => {
  return useQuery<Package[]>({
    queryKey: ['packages', 'featured', limit, packageType],
    queryFn: async () => {
      return apiClient.get<Package[]>(`${endpoints.packages.featured(packageType)}?limit=${limit}`);
    },
  });
};

// Hook for fetching packages by country
export const usePackagesByCountry = (countryId: number) => {
  return useQuery({
    queryKey: ['packages', 'country', countryId],
    queryFn: async () => {
      return apiClient.get<Package[]>(endpoints.packages.byCountry(countryId));
    },
    enabled: !!countryId, // Only run the query if countryId is provided
  });
};



// Hook for fetching a single package by ID
export const usePackage = (id: number) => {
  return useQuery({
    queryKey: ['package', id],
    queryFn: async () => {
      return apiClient.get<Package>(endpoints.packages.detail(id));
    },
    enabled: !!id, // Only run the query if id is provided
  });
};

// Hook for fetching a single package by slug
export const usePackageBySlug = (slug: string) => {
  return useQuery({
    queryKey: ['package', 'slug', slug],
    queryFn: async () => {
      return apiClient.get<Package>(endpoints.packages.bySlug(slug));
    },
    enabled: !!slug, // Only run the query if slug is provided
  });
};

// Hook for fetching package details with gallery by slug
export const usePackageDetailsBySlug = (slug: string) => {
  return useQuery({
    queryKey: ['package-details', slug],
    queryFn: async () => {
      return apiClient.get<PackageWithGallery>(`/api/v1/packages/details/${slug}`);
    },
    enabled: !!slug, // Only run the query if slug is provided
  });
};

// Hook for fetching similar packages
export const useSimilarPackages = (packageId: number, limit: number = 4) => {
  return useQuery({
    queryKey: ['package', packageId, 'similar', limit],
    queryFn: async () => {
      return apiClient.get<Package[]>(`/api/v1/packages/${packageId}/similar?limit=${limit}`);
    },
    enabled: !!packageId,
  });
};

// Hook for fetching recommended packages by country
export const useRecommendedPackages = (countryId: number, currentPackageId: number, limit: number = 4) => {
  return useQuery({
    queryKey: ['packages', 'recommended', countryId, currentPackageId, limit],
    queryFn: async () => {
      const packages = await apiClient.get<Package[]>(`${endpoints.packages.list()}?country_id=${countryId}&limit=${limit + 1}`);
      // Filter out current package
      return packages.filter(pkg => pkg.id !== currentPackageId).slice(0, limit);
    },
    enabled: !!countryId && !!currentPackageId,
  });
};

// Hook for fetching package details with gallery by ID
export const usePackageDetailsById = (id: number) => {
  return useQuery({
    queryKey: ['package-details', 'id', id],
    queryFn: async () => {
      // First get the basic package to get its slug
      const packageData = await apiClient.get<Package>(endpoints.packages.detail(id));
      // Then get the full details with gallery and relationships
      const detailsData = await apiClient.get<any>(`/api/v1/packages/details/${packageData.slug}`);
      // Merge the basic data with details data, ensuring we have all fields needed for editing
      return {
        ...packageData,
        ...detailsData,
        // Ensure we have the country_id from the basic package data
        country_id: packageData.country_id,
        // Map holiday_types to the format expected by the form
        holiday_types: detailsData.holiday_types || []
      };
    },
    enabled: !!id, // Only run the query if id is provided
  });
};

// Hook for creating a new package (admin only)
export const useCreatePackage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (packageData: Omit<Package, 'id' | 'created_at' | 'updated_at' | 'country' | 'holiday_types'>) => {
      return apiClient.post<Package>(endpoints.packages.list(), packageData);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['packages'] });
      queryClient.invalidateQueries({ queryKey: ['packages', 'country', data.country_id] });
    },
  });
};

// Hook for updating a package (admin only)
export const useUpdatePackage = (id: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (packageData: Partial<Package>) => {
      return apiClient.put<Package>(endpoints.packages.detail(id), packageData);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['package', id], data);
      // Invalidate all package queries (including comprehensive by slug)
      queryClient.invalidateQueries({ queryKey: ['package'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['packages'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['package-details'], exact: false });
    },
  });
};

// Hook for partially updating a package (e.g. toggles)
export const usePatchPackage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<Package> }) => {
      return apiClient.patch<Package>(endpoints.packages.patch(id), data);
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(['package', variables.id], data);
      // Invalidate all package queries (including comprehensive by slug)
      queryClient.invalidateQueries({ queryKey: ['package'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['packages'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['package-details'], exact: false });
    },
  });
};

// Hook for deleting a package (admin only)
export const useDeletePackage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      return apiClient.delete(endpoints.packages.delete(id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['packages'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['package'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['package-details'], exact: false });
    },
  });
};

// Hook for fetching packages by holiday type
export const usePackagesByHolidayType = (holidayTypeSlug: string, options?: { skip?: number; limit?: number }) => {
  return useQuery({
    queryKey: ['packages', 'holidayType', holidayTypeSlug, options],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (options?.skip !== undefined) params.append('skip', options.skip.toString());
      if (options?.limit !== undefined) params.append('limit', options.limit.toString());
      params.append('holiday_type', holidayTypeSlug);

      const response = await apiClient.get<Package[]>(`${endpoints.packages.list()}?${params.toString()}`);
      return response;
    },
    enabled: !!holidayTypeSlug,
  });
};

// Hook for fetching comprehensive package details by slug (for tour page redesign)
export const useComprehensivePackageBySlug = (slug: string) => {
  return useQuery({
    queryKey: ['package', 'comprehensive', slug],
    queryFn: async () => {
      return apiClient.get<import('../types/api').PackageDetailResponse>(
        endpoints.packages.comprehensiveBySlug(slug)
      );
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook for reordering packages (admin only)
export const useReorderPackages = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orders: { id: number; order_index: number }[]) => {
      return apiClient.patch('/packages/reorder', { orders });
    },
    // Returning the promise makes mutateAsync wait until fresh data is loaded (no order flicker)
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['packages'], exact: false }),
  });
};
